import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';

/**
 * Support v1 — tickets raised in-product, worked in-product. Fleet users
 * see their own fleet's tickets read-only; the platform owner works the
 * global queue. Mutations are imperative api() calls (FeedbackDialog
 * precedent); callers invalidate via the helpers below.
 */

export type SupportTicketStatus = 'open' | 'replied' | 'closed';

export interface SupportMessage {
  id: string;
  ticketId: string;
  sender: 'reporter' | 'support';
  senderUserId: string | null;
  body: string;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  userId: string | null;
  fleetId: string | null;
  reporterEmail: string | null;
  status: SupportTicketStatus;
  isTest: boolean;
  app: 'work-driver' | 'dispatcher';
  appVersion: string | null;
  build: string | null;
  screen: string | null;
  activeLoadRef: string | null;
  deviceOs: string | null;
  message: string;
  screenshotR2Key: string | null;
  createdAt: string;
  updatedAt: string;
}

export type FleetTicket = SupportTicket & { messages: SupportMessage[] };

const keys = {
  fleet: (fleetId: string) => ['support', 'fleet', fleetId] as const,
};

export function useFleetTickets(fleetId: string | null) {
  return useQuery({
    queryKey: keys.fleet(fleetId ?? 'none'),
    enabled: !!fleetId,
    queryFn: async () => {
      const res = await api<{ tickets: FleetTicket[] }>(
        `/v1/fleets/${fleetId}/support-tickets`,
      );
      return res.tickets;
    },
  });
}

// The platform owner's queue/detail/reply hooks moved to the admin console
// (Phase A U4, law-4 migration) — the fleet-facing rails above are all
// this app still needs.

export function useInvalidateSupport() {
  const qc = useQueryClient();
  return {
    fleet: (fleetId: string) => qc.invalidateQueries({ queryKey: keys.fleet(fleetId) }),
  };
}
